import { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  ImageList,
  ImageListItem,
  IconButton,
  Button,
  Tooltip,
  Fade,
  Skeleton,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DownloadIcon from "@mui/icons-material/GetApp";
import CopyIcon from "@mui/icons-material/FileCopy";
import { Mosaic } from "react-loading-indicators";
import AddIcon from "@mui/icons-material/Add";

import { Link, useNavigate } from "react-router-dom";
import { db } from "./firebase";
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  startAfter,
} from "firebase/firestore";
import LimitDialog from "./LimitDialog";
import ImageDialog from "./ImageDialog";
import { useSnackbar } from "./SnackbarHandler";

const MyImages = ({ user, styles }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const { showSnackbar } = useSnackbar();

  const navigate = useNavigate();

  const [generatedImages, setGeneratedImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [imagesPerPage, setImagesPerPage] = useState(18);
  const [selectedImage, setSelectedImage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isLimitDialogOpen, setIsLimitDialogOpen] = useState(false);

  // Bestäm antalet kolumner baserat på skärmstorlek
  const getImageListCols = () => {
    if (isMobile) return 2;
    if (isTablet) return 3;
    return 4;
  };

  // Create a ref for intersection observer
  const observer = useRef();

  // Last element ref callback for infinite scrolling
  const lastImageElementRef = useCallback(
    (node) => {
      if (loadingMore) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore) {
            loadMoreImages();
          }
        },
        { threshold: 0.5 },
      );

      if (node) observer.current.observe(node);
    },
    [loadingMore, hasMore],
  );

  // Funktion för att hämta bilder med paginering
  const fetchImages = async (isInitialLoad = true) => {
    try {
      if (isInitialLoad) {
        setIsLoading(true);
        setGeneratedImages([]);
      }

      const imagesRef = collection(db, "images");
      let q;

      if (isInitialLoad || !lastDoc) {
        // Initial load
        q = query(
          imagesRef,
          where("userId", "==", user.uid),
          orderBy("createdAt", "desc"),
          limit(imagesPerPage),
        );
      } else {
        // Load more - start after the last document
        q = query(
          imagesRef,
          where("userId", "==", user.uid),
          orderBy("createdAt", "desc"),
          startAfter(lastDoc),
          limit(imagesPerPage),
        );
      }

      const querySnapshot = await getDocs(q);

      // Om vi fick färre resultat än begärt, finns det inga fler att ladda
      if (querySnapshot.docs.length < imagesPerPage) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }

      // Sätt det sista dokumentet för paginering
      const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
      setLastDoc(lastVisible);

      const newImages = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      if (isInitialLoad) {
        setGeneratedImages(newImages);
      } else {
        setGeneratedImages((prevImages) => [...prevImages, ...newImages]);
      }
    } catch (error) {
      console.error("Fel vid hämtning av bilder:", error);
      setError("Kunde inte hämta bilder. Försök igen.");
    } finally {
      setIsLoading(false);
    }
  };

  // Funktion för att ladda fler bilder vid scrollning
  const loadMoreImages = () => {
    if (!hasMore || loadingMore) return;

    // Kontrollera om användaren har nått bildgränsen
    if (generatedImages.length >= imagesPerPage * 1 && !user.isPremium) {
      setIsLimitDialogOpen(true);
      return;
    }

    setLoadingMore(true);

    fetchImages(false).finally(() => {
      setLoadingMore(false);
    });
  };

  // Hantera klick på bild för att öppna dialogen
  const handleImageClick = (image) => {
    setSelectedImage(image);
    setDialogOpen(true);
  };

  // Hantera stängning av bilddialogen
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  // Hantera nerladdning av bild
  const downloadImage = async (imageUrl, filename) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showSnackbar("Bild nedladdad!", "success");
    } catch (error) {
      console.error("Fel vid nedladdning av bild:", error);
      showSnackbar("Kunde inte ladda ner bilden", "error");
    }
  };

  // Hantera kopiering av prompt
  const handleCopyPrompt = (prompt) => {
    navigator.clipboard.writeText(prompt);
    showSnackbar("Prompt kopierad!", "success");
  };

  // Ladda bilder när komponenten monteras
  useEffect(() => {
    if (user?.uid) {
      fetchImages(true);
    }
  }, [user?.uid]);

  // Rendering av bild eller skeleton
  const renderImageOrSkeleton = (item, index) => {
    if (isLoading && index < imagesPerPage) {
      return (
        <Fade in={true} timeout={2000}>
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "100%",
            }}
          >
            <Skeleton
              variant="rectangular"
              width="100%"
              height="100%"
              animation="wave"
              sx={{ borderRadius: "7px" }}
            />
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Mosaic
                color={["#33CCCC", "#33CC36", "#B8CC33", "#1097DA"]}
                size="small"
              />
            </Box>
          </Box>
        </Fade>
      );
    }

    if (!item) return null;

    return (
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          "&:hover .hover-actions": {
            opacity: 1,
          },
        }}
      >
        <img
          src={item.imageUrl}
          alt={item.prompt || "Genererad bild"}
          loading="lazy"
          style={{
            objectFit: "cover",
            width: "100%",
            height: "100%",
            borderRadius: "4px",
            aspectRatio: "1",
          }}
        />

        <Box
          className="hover-actions"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            opacity: 0,
            transition: "opacity 0.3s ease-in-out",
            background: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(2px)",
            borderRadius: "4px",
            "&:hover": {
              opacity: 1,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1,
              padding: "8px",
              borderRadius: "16px",
              backgroundColor: "rgba(255,255,255,0.1)",
            }}
          >
            <Tooltip title="Ladda ned" placement="top">
              <IconButton
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: "white",
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                    transform: "scale(1.1)",
                    transition: "all 0.2s ease-in-out",
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  downloadImage(item.imageUrl, `genererad-bild-${item.id}.png`);
                }}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Kopiera prompt" placement="top">
              <IconButton
                sx={{
                  backgroundColor: theme.palette.secondary.main,
                  color: "white",
                  "&:hover": {
                    backgroundColor: theme.palette.secondary.dark,
                    transform: "scale(1.1)",
                    transition: "all 0.2s ease-in-out",
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyPrompt(item.prompt || item.revisedPrompt);
                }}
              >
                <CopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ ...styles.container }}>
      <Box sx={{ ...styles.innerContainer }}>
        <Box
          sx={{
            ...styles.buttonsContainer,
            ...styles.additionalButtonsContainer,
          }}
        >
          <Button
            variant="purp"
            sx={isMobile ? styles.tightButton : {}}
            onClick={(e) => {
              e.preventDefault();
              navigate("/app/generera-bild");
            }}
            startIcon={<AddIcon />}
          >
            Generera bild
          </Button>
        </Box>
        <Typography
          variant="h1"
          sx={{
            ...styles.newH1,
            pl: 0,
            width: "100%",
            textAlign: "left",
          }}
        >
          Mina bilder
        </Typography>

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <ImageList
          sx={{ width: "100%", gap: "8px !important", overflow: "hidden" }}
          cols={getImageListCols()}
        >
          {isLoading &&
            Array.from(new Array(imagesPerPage)).map((_, index) => (
              <ImageListItem
                key={`skeleton-${index}`}
                sx={{ width: "auto", borderRadius: "3px" }}
              >
                {renderImageOrSkeleton(null, index)}
              </ImageListItem>
            ))}

          {generatedImages.map((item, index) => (
            <ImageListItem
              ref={
                index === generatedImages.length - 1
                  ? lastImageElementRef
                  : null
              }
              onClick={() => handleImageClick(item)}
              key={item.id}
              sx={{
                width: "auto",
                borderRadius: "3px",
                "&:hover": {
                  cursor: "pointer",
                },
              }}
            >
              {renderImageOrSkeleton(item, index + imagesPerPage)}
            </ImageListItem>
          ))}

          {/* Laddningsindikator för fler bilder */}
          {loadingMore && (
            <ImageListItem
              sx={{
                width: "100%",
                height: "80px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress size={30} />
            </ImageListItem>
          )}
        </ImageList>
        {/* Tom tillstånd */}
        {!isLoading && generatedImages.length === 0 && (
          <>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 1, width: "100%" }}
            >
              Du har inte genererat några bilder än. Klicka på 'Generera bild'.
            </Typography>
          </>
        )}
        {/* Bilddialog */}
        <ImageDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          selectedImage={selectedImage}
          onDownload={() => {
            if (selectedImage) {
              downloadImage(
                selectedImage.imageUrl,
                `studera-ai-${selectedImage.id}.png`,
              );
            }
          }}
          onCopyPrompt={() => {
            if (selectedImage) {
              handleCopyPrompt(
                selectedImage.prompt || selectedImage.revisedPrompt,
              );
            }
          }}
          showSnackbar={showSnackbar}
        />

        {/* Begränsningsdialog */}
        <LimitDialog
          open={isLimitDialogOpen}
          onClose={() => setIsLimitDialogOpen(false)}
          userId={user?.uid}
          user={user}
          styles={styles}
          titleText="Uppgradera för att se fler bilder"
        />
      </Box>
    </Box>
  );
};

export default MyImages;
